import { useCallback, useEffect, useRef, useState } from "react";

import { useT } from "../../i18n";
import { cx } from "../../lib/cx";
import { useDataStore } from "../../store/dataStore";

/** Decimal degrees, latitude first — the order the detail panel already uses. */
const PRECISION = 5;

/**
 * Cursor position and zoom, centred on the bottom edge of the map.
 *
 * Sits between the scale bar (bottom-left) and the attribution (bottom-right),
 * the one strip of the map's bottom edge nothing else claims. The timeline is
 * not a concern: it lives outside `main`, below the map, not over it.
 *
 * The readout is written straight into the DOM rather than held in React
 * state. `mousemove` on a map fires at pointer rate, and a `setState` per event
 * would re-render this subtree hundreds of times a second for text that is
 * three spans deep.
 */
export function CoordinateBar() {
  const t = useT();
  const map = useDataStore((s) => s.map);

  const valueRef = useRef<HTMLSpanElement>(null);
  const positionRef = useRef<{ lng: number; lat: number } | null>(null);
  const zoomRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);

  // Mirrors `live` so the mousemove handler can skip setState when nothing
  // changed. React would bail out on an identical value anyway, but not
  // before running the handler's render path on every single pointer event.
  const liveRef = useRef(false);
  const [live, setLive] = useState(false);
  const [copied, setCopied] = useState(false);

  const paint = useCallback(() => {
    const el = valueRef.current;
    if (!el) return;
    const p = positionRef.current;
    const zoom = zoomRef.current;
    const coords = p ? `${p.lat.toFixed(PRECISION)}, ${p.lng.toFixed(PRECISION)}` : "—";
    el.textContent = zoom === null ? coords : `${coords} · z ${zoom.toFixed(1)}`;
  }, []);

  // After every render, not just on mount. React owns the span but never its
  // text, so a re-render for any other reason — switching language is the one
  // that actually happens — would otherwise leave the readout blank.
  useEffect(paint);

  useEffect(() => {
    if (!map) return;

    const schedule = () => {
      if (frameRef.current !== null) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        paint();
      });
    };

    const onMove = (e: { lngLat: { lng: number; lat: number } }) => {
      positionRef.current = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      if (!liveRef.current) {
        liveRef.current = true;
        setLive(true);
      }
      schedule();
    };

    // Keep the last position instead of clearing it: the pointer leaves the
    // map constantly on its way to a panel, and blanking the bar each time
    // reads as a glitch. Dimming says "this is where you last were" without
    // pretending it is still live.
    const onOut = () => {
      if (!liveRef.current) return;
      liveRef.current = false;
      setLive(false);
    };

    const onZoom = () => {
      zoomRef.current = map.getZoom();
      schedule();
    };

    map.on("mousemove", onMove);
    map.on("mouseout", onOut);
    map.on("zoom", onZoom);

    zoomRef.current = map.getZoom();
    paint();

    return () => {
      map.off("mousemove", onMove);
      map.off("mouseout", onOut);
      map.off("zoom", onZoom);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [map, paint]);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(id);
  }, [copied]);

  const copy = () => {
    const p = positionRef.current;
    if (!p) return;
    void navigator.clipboard
      .writeText(`${p.lat.toFixed(PRECISION)}, ${p.lng.toFixed(PRECISION)}`)
      .then(() => setCopied(true))
      // Clipboard access can be refused (permissions, insecure origin). Saying
      // nothing beats claiming a copy that did not happen.
      .catch(() => undefined);
  };

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={t.map.cursorPosition}
      title={t.map.copyCoordinates}
      className={cx(
        "hidden items-center gap-2 rounded-md border border-border-subtle bg-surface-raised/80",
        "px-2 py-0.5 text-xs shadow-md backdrop-blur transition-opacity md:flex",
        "hover:text-text-primary",
        live ? "text-text-secondary" : "text-text-tertiary opacity-60",
      )}
    >
      <span ref={valueRef} className="data-value" />
      {copied && <span className="text-accent">{t.map.coordinatesCopied}</span>}
    </button>
  );
}
